import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number[]} */
  #renderFixTimers = [];

  /** @type {number | undefined} */
  #latestKnownCartCount = undefined;

  connectedCallback() {
    super.connectedCallback();
    this.#injectDrawerAnimationStyle();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#clearRenderFixTimers();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    this.#latestKnownCartCount = event.detail.resource?.item_count;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  // FB FIX: Adet degisimi / satir silme sonrasi gercek itemCount'u koru; header bubble stale/hidden kalmasin.
  #handleCartUpdate = (event) => {
    const data = event.detail?.data;
    if (data?.source === 'cart-items-component' && typeof data.itemCount === 'number') {
      this.#latestKnownCartCount = data.itemCount;
    }

    this.#scheduleDrawerRenderFix();
  };

  // FB FIX: Acilista CSS animasyonu PURUZSUZ aksin diye agir JS isini slayd bittikten SONRA calistir.
  #handleDialogOpen = () => {
    this.#clearRenderFixTimers();

    [480, 640].forEach((delay) => {
      const timer = window.setTimeout(() => this.#forceDrawerRender(), delay);
      this.#renderFixTimers.push(timer);
    });
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  // FB FIX: Sepet drawer'i icin yumusak ac/kapa animasyonu + ufak gorsel iyilestirmeler (head'e bir kez enjekte).
  #injectDrawerAnimationStyle() {
    if (document.getElementById('fb-cart-drawer-anim-style')) return;

    const style = document.createElement('style');
    style.id = 'fb-cart-drawer-anim-style';
    style.textContent = [
      'cart-drawer-component dialog{will-change:transform!important;backface-visibility:hidden!important;-webkit-backface-visibility:hidden!important;}',
      'cart-drawer-component dialog[open]:not(.dialog-closing){animation-duration:300ms!important;animation-timing-function:cubic-bezier(0.4,0,0.2,1)!important;}',
      'cart-drawer-component dialog[open].dialog-closing{animation-duration:250ms!important;animation-timing-function:cubic-bezier(0.3,0,0.8,0.15)!important;}',
      'cart-drawer-component dialog::backdrop{animation-duration:300ms!important;transition-duration:300ms!important;animation-timing-function:ease!important;transition-timing-function:ease!important;}',
      'cart-drawer-component dialog[open]:not(.cart-drawer--empty) .cart-drawer__summary,cart-drawer-component dialog[open]:not(.cart-drawer--empty) .cart__ctas{visibility:visible!important;opacity:1!important;pointer-events:auto!important;}',
      'cart-drawer-component dialog[open]:not(.cart-drawer--empty) .cart__ctas{display:flex!important;flex-direction:column!important;align-items:stretch!important;width:100%!important;max-width:100%!important;}',
      'cart-drawer-component dialog[open]:not(.cart-drawer--empty) .cart__checkout-button,cart-drawer-component dialog[open]:not(.cart-drawer--empty) button[name="checkout"]{display:flex!important;align-items:center!important;justify-content:center!important;width:100%!important;max-width:100%!important;visibility:visible!important;opacity:1!important;pointer-events:auto!important;}',
      '@media(min-width:750px){cart-drawer-component dialog[open]:not(.cart-drawer--empty) .cart__checkout-button,cart-drawer-component dialog[open]:not(.cart-drawer--empty) button[name="checkout"]{background:linear-gradient(var(--fb-cart-pc-checkout-angle,var(--fb-cart-checkout-angle,90deg)),var(--fb-cart-pc-checkout-bg-1,var(--fb-cart-checkout-bg-1,#D97651)),var(--fb-cart-pc-checkout-bg-2,var(--fb-cart-checkout-bg-2,#9E5135)))!important;color:var(--fb-cart-pc-checkout-text,var(--fb-cart-checkout-text,#F5EAD8))!important;-webkit-text-fill-color:var(--fb-cart-pc-checkout-text,var(--fb-cart-checkout-text,#F5EAD8))!important;border-color:transparent!important;box-shadow:none!important;}cart-drawer-component dialog[open]:not(.cart-drawer--empty) .cart__checkout-button *,cart-drawer-component dialog[open]:not(.cart-drawer--empty) button[name="checkout"] *{color:var(--fb-cart-pc-checkout-text,var(--fb-cart-checkout-text,#F5EAD8))!important;-webkit-text-fill-color:var(--fb-cart-pc-checkout-text,var(--fb-cart-checkout-text,#F5EAD8))!important;}}',
      'text-component[shimmer]{color:inherit!important;animation:fb-soft-shimmer 1.1s ease-in-out infinite!important;}',
      'text-component[shimmer]::after{display:none!important;animation:none!important;opacity:0!important;}',
      '@keyframes fb-soft-shimmer{0%,100%{opacity:1}50%{opacity:0.5}}',
      'cart-drawer-component .cart-items__table-row.removing{animation-duration:160ms!important;animation-delay:0ms!important;}',
      'cart-drawer-component .cart-items__table-row.removing{height:0!important;min-height:0!important;max-height:0!important;padding:0!important;margin:0!important;opacity:0!important;overflow:hidden!important;border:0!important;pointer-events:none!important;}',
      'html,body{scrollbar-width:none!important;-ms-overflow-style:none!important;}',
      'html::-webkit-scrollbar,body::-webkit-scrollbar{width:0!important;height:0!important;display:none!important;}'
    ].join('');
    document.head.appendChild(style);
  }

  #clearRenderFixTimers() {
    this.#renderFixTimers.forEach((timer) => window.clearTimeout(timer));
    this.#renderFixTimers = [];
  }

  #scheduleDrawerRenderFix = () => {
    this.#clearRenderFixTimers();

    requestAnimationFrame(() => {
      this.#forceDrawerRender();

      requestAnimationFrame(() => {
        this.#forceDrawerRender();
      });
    });

    [90, 180].forEach((delay) => {
      const timer = window.setTimeout(() => {
        this.#forceDrawerRender();
      }, delay);

      this.#renderFixTimers.push(timer);
    });
  };

  #forceDrawerRender() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog?.open) return;

    const hasCartForm = Boolean(dialog.querySelector('.cart-form'));
    const hasRows = Boolean(
      dialog.querySelector('.cart-items__table-row[data-key]') ||
        dialog.querySelector('[ref="cartItemRows[]"]')
    );
    const hasItems = hasCartForm || hasRows;

    if (!hasItems) {
      this.#updateStickyState();
      return;
    }

    dialog.classList.remove('cart-drawer--empty');
    dialog.setAttribute('aria-labelledby', 'cart-drawer-heading');

    const summary = dialog.querySelector('.cart-drawer__summary');
    const ctas = dialog.querySelector('.cart__ctas');
    const checkoutButtons = dialog.querySelectorAll(
      ".cart__checkout-button, button[name='checkout'], .button[name='checkout']"
    );

    if (summary instanceof HTMLElement) {
      summary.removeAttribute('hidden');
      summary.style.setProperty('display', 'flex', 'important');
      summary.style.setProperty('visibility', 'visible', 'important');
      summary.style.setProperty('opacity', '1', 'important');
      summary.style.setProperty('pointer-events', 'auto', 'important');
    }

    if (ctas instanceof HTMLElement) {
      ctas.removeAttribute('hidden');
      ctas.style.setProperty('display', 'flex', 'important');
      ctas.style.setProperty('flex-direction', 'column', 'important');
      ctas.style.setProperty('align-items', 'stretch', 'important');
      ctas.style.setProperty('justify-content', 'flex-end', 'important');
      ctas.style.setProperty('width', '100%', 'important');
      ctas.style.setProperty('max-width', '100%', 'important');
      ctas.style.setProperty('visibility', 'visible', 'important');
      ctas.style.setProperty('opacity', '1', 'important');
      ctas.style.setProperty('overflow', 'visible', 'important');
      ctas.style.setProperty('pointer-events', 'auto', 'important');
    }

    checkoutButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;

      button.removeAttribute('hidden');
      button.style.setProperty('display', 'flex', 'important');
      button.style.setProperty('align-items', 'center', 'important');
      button.style.setProperty('justify-content', 'center', 'important');
      button.style.setProperty('width', '100%', 'important');
      button.style.setProperty('max-width', '100%', 'important');
      button.style.setProperty('visibility', 'visible', 'important');
      button.style.setProperty('opacity', '1', 'important');
      button.style.setProperty('pointer-events', 'auto', 'important');
      button.style.setProperty('filter', 'none', 'important');
      button.style.setProperty('transform', 'none', 'important');
    });

    const cartCount = this.#getDrawerCartCount(dialog);

    dialog.querySelectorAll('.cart-drawer__heading .cart-bubble').forEach((bubble) => {
      if (!(bubble instanceof HTMLElement)) return;

      if (cartCount) {
        bubble.setAttribute('data-cart-count', cartCount);
      }

      bubble.style.setProperty('display', 'inline-flex', 'important');
      bubble.style.setProperty('visibility', 'visible', 'important');
      bubble.style.setProperty('opacity', '1', 'important');
      bubble.style.setProperty('pointer-events', 'none', 'important');

      bubble.querySelectorAll('.cart-bubble__text-count').forEach((countText) => {
        if (cartCount) countText.textContent = cartCount;
      });
    });

    // FB FIX: Header sepet bubble'i data-cart-count ile ciziliyor. Satir silmede atributu silme;
    // gercek kalan itemCount'u koru ve hidden state'ini senkronla. Drawer heading bubble'ina dokunma.
    document.querySelectorAll('header-actions .header-actions__cart-icon .cart-bubble').forEach((bubble) => {
      if (!(bubble instanceof HTMLElement)) return;

      const headerIcon = bubble.closest('.header-actions__cart-icon');
      const hasCount = Boolean(cartCount && Number(cartCount) > 0);

      if (hasCount) {
        bubble.setAttribute('data-cart-count', cartCount);
        bubble.classList.remove('visually-hidden');
      } else {
        bubble.removeAttribute('data-cart-count');
        bubble.classList.add('visually-hidden');
      }

      if (headerIcon instanceof HTMLElement) {
        headerIcon.classList.toggle('header-actions__cart-icon--has-cart', hasCount);
      }

      bubble.style.setProperty('display', 'inline-flex', 'important');
      bubble.style.setProperty('visibility', 'visible', 'important');
      bubble.style.setProperty('opacity', '1', 'important');
      bubble.style.setProperty('pointer-events', 'none', 'important');

      bubble.querySelectorAll('.cart-bubble__text-count').forEach((countText) => {
        countText.textContent = hasCount ? cartCount : '';
        countText.classList.toggle('hidden', !hasCount);
      });
    });

    if (window.matchMedia('(min-width: 750px)').matches) {
      const rootStyle = getComputedStyle(document.documentElement);
      const cssVar = (name, fallback) => {
        const v = rootStyle.getPropertyValue(name).trim();
        return v || fallback;
      };
      const bg1 = cssVar('--fb-cart-pc-checkout-bg-1', cssVar('--fb-cart-checkout-bg-1', '#D97651'));
      const bg2 = cssVar('--fb-cart-pc-checkout-bg-2', cssVar('--fb-cart-checkout-bg-2', '#9E5135'));
      const angle = cssVar('--fb-cart-pc-checkout-angle', cssVar('--fb-cart-checkout-angle', '90deg'));
      const text = cssVar('--fb-cart-pc-checkout-text', cssVar('--fb-cart-checkout-text', '#F5EAD8'));
      const gradient = `linear-gradient(${angle}, ${bg1}, ${bg2})`;

      checkoutButtons.forEach((button) => {
        if (!(button instanceof HTMLElement)) return;

        button.style.setProperty('--button-background-color', bg1, 'important');
        button.style.setProperty('--button-color', text, 'important');
        button.style.setProperty('--button-border-color', 'transparent', 'important');
        button.style.setProperty('background', gradient, 'important');
        button.style.setProperty('background-image', gradient, 'important');
        button.style.setProperty('background-color', bg1, 'important');
        button.style.setProperty('color', text, 'important');
        button.style.setProperty('-webkit-text-fill-color', text, 'important');
        button.style.setProperty('border-color', 'transparent', 'important');
        button.style.setProperty('box-shadow', 'none', 'important');

        button.querySelectorAll('*').forEach((child) => {
          if (!(child instanceof HTMLElement)) return;
          child.style.setProperty('color', text, 'important');
          child.style.setProperty('-webkit-text-fill-color', text, 'important');
        });
      });

      this.querySelectorAll('.cart-drawer__heading').forEach((heading) => {
        if (!(heading instanceof HTMLElement)) return;

        if (cartCount) {
          heading.setAttribute('data-fb-drawer-count', cartCount);
        } else {
          heading.removeAttribute('data-fb-drawer-count');
        }
      });
    }

    this.#updateStickyState();

    document.dispatchEvent(
      new CustomEvent('fb:cart-drawer-render-fixed', {
        bubbles: true,
        detail: {
          item_count: cartCount ? Number(cartCount) : undefined,
        },
      })
    );
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @returns {string}
   */
  #getDrawerCartCount(dialog) {
    if (typeof this.#latestKnownCartCount === 'number') {
      return String(this.#latestKnownCartCount);
    }

    let total = 0;

    dialog.querySelectorAll('input[type="number"], input[name^="updates"], input[name*="updates"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const value = Number.parseInt(input.value, 10);
      if (!Number.isNaN(value) && value > 0) {
        total += value;
      }
    });

    if (total > 0) {
      return String(total);
    }

    const rows = dialog.querySelectorAll('.cart-items__table-row[data-key], [ref="cartItemRows[]"]');
    if (rows.length > 0) {
      return String(rows.length);
    }

    return '';
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}